import React, { useEffect, useState } from "react";
import { Col, Container, Row, Form, Button } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import Nav from "react-bootstrap/Nav";
import Tab from "react-bootstrap/Tab";
import { useDispatch } from "react-redux";
import {
  getMedallionDetails,
  uploadFile,
  updateMedallion,
  createTribute,
} from "../redux/actions/profile";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Layout from "../components/Layout";
import { toast } from "react-toastify";
import GalleryModal from "../components/GalleryModal";
import FamilyModal from "../components/FamilyModal";
import TributeModal from "../components/TributeModal";
import TributeSection from "../components/TributeSection";

export default function EditProfile() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [medallionData, setMedallionData] = useState(null);
  const [name, setName] = useState("");
  const [born, setBorn] = useState("");
  const [died, setDied] = useState("");
  const [biographyTitle, setBiographyTitle] = useState("");
  const [biographyContent, setBiographyContent] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerImagePreview, setBannerImagePreview] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showTributeModal, setShowTributeModal] = useState(false);
  const [isGalleryUploading, setIsGalleryUploading] = useState(false);

  useEffect(() => {
    const fetchMedallionDetails = async () => {
      try {
        const response = await dispatch(getMedallionDetails(id)).unwrap();
        setMedallionData(response.data);
        setName(response.data?.name || "");
        setBorn(response.data?.born || "");
        setDied(response.data?.died || "");
        setBiographyTitle(response.data?.bio?.title || "");
        setBiographyContent(response.data?.bio?.description || "");
        setGalleryItems(response.data?.gallery || []);
        setFamilyMembers(response.data?.family || []);
      } catch (error) {
        console.error("Error fetching medallion details:", error);
      }
    };

    if (id) {
      fetchMedallionDetails();
    }
  }, [id, dispatch]);

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setProfileImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    const response = await dispatch(uploadFile(formData)).unwrap();
    return response.data?.image;
  };

  const handleSubmit = async () => {
    try {
      const updateData = {
        name,
        born,
        died,
        bio: {
          title: biographyTitle,
          description: biographyContent,
        },
      };

      if (profileImage) {
        const profileImageUrl = await uploadImage(profileImage);
        updateData.profile_image = profileImageUrl;
      }

      if (bannerImage) {
        console.log('Uploading banner image:', bannerImage);
        const bannerImageUrl = await uploadImage(bannerImage);
        console.log('Banner image uploaded, URL:', bannerImageUrl);
        updateData.banner_image = bannerImageUrl;
      }

      console.log("Update payload:", updateData);
      const response = await dispatch(
        updateMedallion({ id, data: updateData })
      ).unwrap();

      // Update medallionData with new image URLs
      setMedallionData((prev) => ({
        ...prev,
        ...updateData,
        profile_image: updateData.profile_image || prev?.profile_image,
        banner_image: updateData.banner_image || prev?.banner_image,
      }));

      toast.success("Profile updated successfully!");
      setProfileImage(null);
      setProfileImagePreview(null);
      setBannerImage(null);
      setBannerImagePreview(null);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  const handleBannerImageChange = async (e) => {
    const file = e.target.files[0];
    console.log('Banner file selected:', file);
    if (file) {
      setBannerImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('Banner preview set');
        setBannerImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      try {
        console.log('Uploading banner image immediately:', file);
        const bannerImageUrl = await uploadImage(file);
        console.log('Banner image uploaded, URL:', bannerImageUrl);
        
        const response = await dispatch(
          updateMedallion({ id, data: { banner_image: bannerImageUrl } })
        ).unwrap();
        
        setMedallionData((prev) => ({
          ...prev,
          banner_image: bannerImageUrl,
        }));
        
        toast.success("Banner updated successfully!");
        // setBannerImage(null);
        // setBannerImagePreview(null);
      } catch (error) {
        console.error('Error uploading banner image:', error);
        toast.error('Failed to update banner');
      }
    }
  };

  const handleGalleryFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    const fileData = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith("video/") ? "video" : "image",
    }));
    setSelectedFiles((prev) => [...prev, ...fileData]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGallerySubmit = async () => {
    try {
      setIsGalleryUploading(true);
      const uploadedUrls = [];

      for (const fileData of selectedFiles) {
        const url = await uploadImage(fileData.file);
        uploadedUrls.push(url);
      }

      const updatedGallery = [...galleryItems, ...uploadedUrls];

      await dispatch(
        updateMedallion({
          id,
          data: { gallery: updatedGallery },
        })
      ).unwrap();

      setGalleryItems(updatedGallery);
      setSelectedFiles([]);
      setShowGalleryModal(false);
      toast.success("Gallery updated successfully!");
    } catch (error) {
      console.error("Error updating gallery:", error);
      toast.error("Failed to update gallery");
    } finally {
      setIsGalleryUploading(false);
    }
  };

  const handleFamilySubmit = async (values) => {
    try {
      const newFamilyMember = {
        relation: values.relation,
        name: values.names.filter((name) => name.trim() !== ""),
      };

      const updatedFamily = [...familyMembers, newFamilyMember];

      await dispatch(
        updateMedallion({
          id,
          data: { family: updatedFamily },
        })
      ).unwrap();

      setFamilyMembers(updatedFamily);
      setShowFamilyModal(false);
      toast.success("Family member added successfully!");
    } catch (error) {
      console.error("Error adding family member:", error);
      toast.error("Failed to add family member");
    }
  };

  const handleTributeSubmit = async (values) => {
    setShowTributeModal(false);
    try {
      await dispatch(
        createTribute({
          medallionId: id,
          data: values,
        })
      ).unwrap();

      setShowTributeModal(false);
      toast.success("Tribute submitted successfully!");
    } catch (error) {
      console.error("Error submitting tribute:", error);
      toast.error("Failed to submit tribute");
    }
  };

  return (
    <Layout>
      <section className=" profile-banner-sec-edit">
        <div className="">
          <img
            src={
              bannerImagePreview ||
              medallionData?.banner_image ||
              require("../assets/images/profile-bg.png")
            }
          />
          <div>
            <button
              type="button"
              onClick={() => {
                console.log('Edit Banner button clicked');
                const input = document.getElementById("bannerInput");
                console.log('Banner input element:', input);
                if (input) {
                  input.click();
                } else {
                  console.error('Banner input not found');
                }
              }}
            >
              Edit Banner
            </button>
            <input
              id="bannerInput"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleBannerImageChange}
            />
          </div>
        </div>
      </section>
      <section className="profile-sec-content">
        <Container>
          <div className="profile-sec-data">
            <div className="profile-sec-data">
              <img
                src={
                  profileImagePreview ||
                  medallionData?.profile_image ||
                  require("../assets/images/sarahjohnson.png")
                }
              />
              <div
                className="edit-icon"
                onClick={() => document.getElementById("profileInput").click()}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <input
                id="profileInput"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleProfileImageChange}
              />
            </div>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter name"
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  textAlign: "center",
                  border: "none",
                  background: "transparent",
                }}
              />
            </Form.Group>
            <div className="profile-born-died">
              <Form.Group>
                <Form.Label>
                  <b>Born:</b>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={born}
                  onChange={(e) => setBorn(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  <b>Died:</b>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={died}
                  onChange={(e) => setDied(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="mt-3">
              <Tab.Container
                id="left-tabs-example"
                defaultActiveKey="Biography"
              >
                <div className="main-profile-tab">
                  <div className="main-profile-top">
                    <Nav variant="pills" className="respo-main-profile-top">
                      <Nav.Item>
                        <Nav.Link eventKey="Biography">Biography</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="Gallery">Gallery</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="Family">Family</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="Tribute">Tribute</Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </div>
                  <div className="main-profile-bottom">
                    <Tab.Content>
                      <Tab.Pane eventKey="Biography">
                        <div className="biography-data">
                          <Form.Group className="mb-3">
                            <Form.Label>Biography Title</Form.Label>
                            <Form.Control
                              type="text"
                              value={biographyTitle}
                              onChange={(e) =>
                                setBiographyTitle(e.target.value)
                              }
                              placeholder="Enter biography title"
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Biography Content</Form.Label>
                            <ReactQuill
                              value={biographyContent}
                              onChange={setBiographyContent}
                              modules={{
                                toolbar: [
                                  [{ header: [1, 2, false] }],
                                  ["bold", "italic", "underline"],
                                  ["link"],
                                  [{ list: "ordered" }, { list: "bullet" }],
                                  ["clean"],
                                ],
                              }}
                              placeholder="Write biography content..."
                            />
                          </Form.Group>
                          <div className="find-a-memory-action">
                            <button onClick={handleSubmit}>Save Changes</button>
                          </div>
                        </div>
                      </Tab.Pane>
                      <Tab.Pane eventKey="Gallery">
                        <div className="gallery-data">
                          <div className="gallery-data-head">
                            <button onClick={() => setShowGalleryModal(true)}>
                              Add Images
                            </button>
                            <input type="date" />
                          </div>
                          <div className="gallery-data-photos">
                            {galleryItems?.filter(item => item !== null).map((item, index) => {
                              const isVideo = item.includes('.mp4') || item.includes('.mov') || item.includes('.avi') || item.includes('.webm');
                              return isVideo ? (
                                <video key={index} src={item} controls style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                              ) : (
                                <img key={index} src={item} alt={`Gallery ${index}`} />
                              );
                            }) || <p>No gallery items available.</p>}
                          </div>
                        </div>
                      </Tab.Pane>
                      <Tab.Pane eventKey="Family">
                        <div className="family-datad-conatin">
                          <div className="mb-3">
                            <button
                              className=""
                              onClick={() => setShowFamilyModal(true)}
                            >
                              Add Family Member
                            </button>
                          </div>
                          <div className="d-flex justify-content-between">
                            <div className="w-50 pe-3">
                              <ul>
                                {familyMembers
                                  .filter((_, index) => index % 2 === 0)
                                  .map((member, index) => (
                                    <li key={index} className="mb-3">
                                      <h3>{member.relation}</h3>
                                      {member.name.map((name, nameIndex) => (
                                        <p key={nameIndex}>{name}</p>
                                      ))}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                            <div className="w-50 ps-3">
                              <ul>
                                {familyMembers
                                  .filter((_, index) => index % 2 === 1)
                                  .map((member, index) => (
                                    <li key={index} className="mb-3">
                                      <h3>{member.relation}</h3>
                                      {member.name.map((name, nameIndex) => (
                                        <p key={nameIndex}>{name}</p>
                                      ))}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </Tab.Pane>
                      {/* <Tab.Pane eventKey="Tribute">
                        <div className="tribute-data">
                          <div className="d-flex justify-content-start gap-3">
                            <button onClick={() => setShowTributeModal(true)}>
                              Submit a Tribute
                            </button>
                            <button>Send Gifts</button>
                          </div>
                          <div className="tribute-cards">
                            <div className="tribute-items">
                              <div className="">
                                <h5>Garry Eisenberg</h5>
                                <p>15th June 2024</p>
                              </div>
                              <div className="">
                                <p>
                                  Evelyn was a true beacon of hope and
                                  resilience. Her tireless dedication to social
                                  justice and unwavering compassion transformed
                                  countless lives. Evelyn's legacy of kindness,
                                  courage, and advocacy will continue to inspire
                                  us all
                                </p>
                              </div>
                            </div>
                            <div className="tribute-items">
                              <div className="">
                                <h5>Garry Eisenberg</h5>
                                <p>15th June 2024</p>
                              </div>
                              <div className="">
                                <p>
                                  Evelyn was a true beacon of hope and
                                  resilience. Her tireless dedication to social
                                  justice and unwavering compassion transformed
                                  countless lives. Evelyn's legacy of kindness,
                                  courage, and advocacy will continue to inspire
                                  us all
                                </p>
                              </div>
                            </div>
                            <div className="tribute-items">
                              <div className="">
                                <h5>Garry Eisenberg</h5>
                                <p>15th June 2024</p>
                              </div>
                              <div className="">
                                <p>
                                  Evelyn was a true beacon of hope and
                                  resilience. Her tireless dedication to social
                                  justice and unwavering compassion transformed
                                  countless lives. Evelyn's legacy of kindness,
                                  courage, and advocacy will continue to inspire
                                  us all
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Tab.Pane> */}
                      <Tab.Pane eventKey="Tribute">
                        <TributeSection medallionId={id} />
                      </Tab.Pane>
                    </Tab.Content>
                  </div>
                </div>
              </Tab.Container>
            </div>
          </div>
        </Container>
      </section>

      <GalleryModal
        show={showGalleryModal}
        onHide={() => setShowGalleryModal(false)}
        selectedFiles={selectedFiles}
        onFilesSelect={handleGalleryFilesSelect}
        onRemoveFile={removeSelectedFile}
        onSubmit={handleGallerySubmit}
        isUploading={isGalleryUploading}
      />

      <FamilyModal
        show={showFamilyModal}
        onHide={() => setShowFamilyModal(false)}
        onSubmit={handleFamilySubmit}
      />

      <TributeModal
        show={showTributeModal}
        onHide={() => setShowTributeModal(false)}
        onSubmit={handleTributeSubmit}
      />
    </Layout>
  );
}
